import { useConversationSidePanelContext } from "@app/components/assistant/conversation/ConversationSidePanelContext";
import { extractPlanTitle } from "@app/components/assistant/conversation/plan_mode/utils";
import { ConfirmContext } from "@app/components/Confirm";
import { AppLayoutTitle } from "@app/components/sparkle/AppLayoutTitle";
import {
  useClosePlan,
  usePlanFile,
} from "@app/hooks/conversations/usePlanFile";
import type { ConversationWithoutContentType } from "@app/types/assistant/conversation";
import type { LightWorkspaceType } from "@app/types/user";
import { Archive, Button, Markdown, Spinner, XClose } from "@dust-tt/sparkle";
import { Trans, useLingui } from "@lingui/react/macro";
import { useContext } from "react";

interface ConversationPlanModePanelProps {
  conversation: ConversationWithoutContentType;
  owner: LightWorkspaceType;
}

export function ConversationPlanModePanel({
  conversation,
  owner,
}: ConversationPlanModePanelProps) {
  const { t } = useLingui();
  const { closePanel } = useConversationSidePanelContext();
  const { content, isPlanLoading } = usePlanFile({
    conversationId: conversation.sId,
    workspaceId: owner.sId,
  });
  const { closePlan, isClosing } = useClosePlan({
    workspaceId: owner.sId,
    conversationId: conversation.sId,
  });
  const confirm = useContext(ConfirmContext);

  // Sits next to the panel close button, so ask before archiving.
  const archivePlan = async () => {
    const confirmed = await confirm({
      title: t`Archive this plan?`,
      message: t`This will hide the current plan from the side panel, but keep it in the conversation's files. The agent can create a new one.`,
      validateLabel: t`Archive plan`,
      validateVariant: "primary",
    });
    if (confirmed) {
      await closePlan();
    }
  };

  const title = extractPlanTitle(content) ?? t`Untitled plan`;

  return (
    <div className="flex h-panel flex-col">
      <AppLayoutTitle>
        <div className="flex h-full items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-sm font-semibold text-foreground">
              <Trans>Plan: {title}</Trans>
            </span>
          </div>
          <div className="flex items-center gap-1">
            {content && (
              <Button
                variant="ghost"
                size="sm"
                icon={Archive}
                tooltip={t`Archive plan`}
                isLoading={isClosing}
                onClick={() => void archivePlan()}
              />
            )}
            <Button
              tooltip={t`Close`}
              variant="ghost"
              size="sm"
              onClick={closePanel}
              icon={XClose}
            />
          </div>
        </div>
      </AppLayoutTitle>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {isPlanLoading && !content ? (
          <div className="flex h-full items-center justify-center">
            <Spinner />
          </div>
        ) : !content ? (
          <div className="text-sm text-muted-foreground">
            <Trans>No active plan for this conversation.</Trans>
          </div>
        ) : (
          // Plain (non-memoized) blocks so each edit re-renders items in place and the step
          // badges can transition when the agent ticks a task. Items are matched by position,
          // so inserting a task above completed ones replays their check animation once.
          <Markdown
            content={content}
            taskListVariant="step"
            optimizeForStreaming={false}
          />
        )}
      </div>
    </div>
  );
}
